import { Suspense, type ReactNode } from 'react';
import { useLingui } from '@lingui/react/macro';
import { useSuspenseQuery } from '@tanstack/react-query';
import { trpc } from '@/lib/trpc';
import { Image } from '@/routes/-components/image';
import { diamondGlows, DiamondGrid } from '../primitives';
import { PartnerLogo } from '../partner-logo';
import { LottiePlayer } from '../lottie/lottie-player';
import { usePrefersReducedMotion } from '../use-prefers-reduced-motion';
import { Expand } from '../expand';
import mailboxAnimation from '../lottie/contact-mailbox.json';
import { CallIcon, LocationIcon } from './icons';
import type { Audience } from './audiences';

/** The office, as the hand-off writes it. `www/contact` still says 108 — 112 is the number to keep. */
const ADDRESS_LINES = ['Carrer de Llull, 112, Sant Martí, 08005', 'Barcelona, España.'];
const MAPS_URL = 'https://maps.google.com/?q=Carrer+de+Llull+112+08005+Barcelona';
/** Human form for the label, E.164 for the link. */
const PHONE_DISPLAY = '(+34) 936 11 01 04';
const PHONE_HREF = 'tel:+34936110104';

/**
 * The hand-off's dotted areas are all the same 777x501 ellipse of 4.39px diamonds on a 27.59px pitch,
 * which is what `DiamondGrid` models — so the exports are not shipped, the primitive redraws them.
 * Both numbers are measured off this page's own export, not rounded to the homepage's 5/27.
 */
const DIAMOND_TILE = { size: 4.39, gap: 27.59 } as const;

/** As many as the strip fits at the designed width without the logos shrinking to nothing. */
const LOGO_COUNT = 6;

/**
 * The NGOs the strip names, in the order it draws them — communications' choice, not a ranking.
 *
 * Pinned by ID rather than by name or by a shipped image file. The RECORD is still what gets drawn
 * (see `NgoLogos`), so a partner that reworks its mark in the backoffice updates the page; what is
 * fixed here is only which five. By id because the names are not distinctive: the table also holds a
 * "CREU ROJA", a "Cruz Roja Chilena" and a "UNICEF Andorra", which are different entities from these.
 */
export const CONTACT_NGO_IDS = [
	'8d9d61a5-cff7-4c4c-a439-f2632cd33de7', // Cruz Roja
	'cd54c853-c0a2-4726-91d8-a4fb3e23cc81', // Fundación Ana Bella
	'73fbaead-f1c2-4444-9d44-2f5ff8b39c4e', // DOWN España
	'c051d936-35e7-497b-bb21-44e44570bcd9', // Hospital Sant Joan de Déu
	'e16293db-50b7-4241-92c0-3b52d050b807', // UNICEF
] as const;

/**
 * The plates themselves. Unboxed, a deliberate departure from the hand-off's linen card — and in the
 * partners' own colours, which is now `PartnerLogo`'s behaviour everywhere rather than this strip's.
 */
function LogoStrip(props: { label: string; logos: Array<{ id: string; name?: string; location?: string }> }) {
	if (props.logos.length === 0) return null;
	return (
		<ul aria-label={props.label} className="flex h-[104px] items-center justify-between gap-6 md:gap-10">
			{props.logos.map((logo) => (
				<li key={logo.id} className="flex h-16 min-w-0 flex-1 items-center justify-center">
					<PartnerLogo src={logo.location} alt={logo.name ?? logo.id} size="lg" />
				</li>
			))}
		</ul>
	);
}

/**
 * What a COMPANY sees: the brands ALREADY doing this, off the same stats the homepage and Solutions
 * render — a measured list, so it can never name a merchant we no longer work with.
 */
function MerchantLogos() {
	const { t } = useLingui();
	const { data: stats } = useSuspenseQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());
	const logos = stats.partnerMerchants
		.filter((merchant) => merchant.logo)
		.slice(0, LOGO_COUNT)
		.map((merchant) => ({ id: merchant.id, name: merchant.name, location: merchant.logo?.location }));
	return <LogoStrip label={t`Partner company logos`} logos={logos} />;
}

/**
 * What everybody else sees, an undecided visitor included: the NGOs the money reaches — the five
 * `CONTACT_NGO_IDS` names.
 *
 * It was the top of a BI ranking by amount raised until communications picked these five. The ranking
 * is honest but it is not an ARGUMENT: it put whoever happened to lead that month beside a page asking
 * the visitor to get in touch, and where BI has no stats it degrades to alphabetical, which is how the
 * strip came to open on "A. P. S. Piccoli Battiti".
 *
 * The hand-off pictures ten hand-picked black lockups, two of which (Amnesty, Greenpeace) are not
 * Worldcoo partners at all. Reading the chosen five out of the NGO table rather than shipping their
 * artwork is what keeps that from happening again: an id only draws if the record behind it is one we
 * may present, and an id that stops resolving drops out rather than breaking the row — so the strip is
 * allowed to come back short. See `getNgoLogos`.
 */
function NgoLogos() {
	const { t } = useLingui();
	const { data } = useSuspenseQuery(trpc.portal.getNgoLogos.queryOptions({ ngoIds: [...CONTACT_NGO_IDS] }));
	const logos = data.ngos.map((ngo) => ({ id: ngo.id, name: ngo.name, location: ngo.logo.location }));
	return <LogoStrip label={t`Partner NGO logos`} logos={logos} />;
}

/**
 * The strip between the photograph and the address card, and it answers the question the panel just
 * asked: a company sees the brands already doing this, everybody else sees the NGOs the money reaches.
 *
 * Each half fetches for itself, and the Suspense boundary wraps the strip alone: the photograph above
 * it and the address below need no data and must not wait for one.
 */
function PartnerLogos({ audience }: { audience: Audience | null }) {
	return audience === 'company' ? <MerchantLogos /> : <NgoLogos />;
}

/** One "eyebrow + value" pair on the address card: a 16px glyph, its faint label, then the value. */
function ContactDetail({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
	return (
		<div className="relative flex flex-col gap-2">
			{/* The glyph sits ABOVE its label, not beside it — the hand-off stacks them with a 4px gap. */}
			<div className="text-ink flex flex-col gap-1 opacity-50">
				{icon}
				<span className="text-sm font-medium tracking-[-0.02em]">{label}</span>
			</div>
			{children}
		</div>
	);
}

/**
 * 369x358 starting at 51% of the card's width and 26px down — the hand-off's own placement, which runs
 * the post off the bottom edge, so the card's clipping is load-bearing. Kept in percentages of the card
 * so it scales with it instead of drifting off the corner. The aspect is stated rather than left to the
 * image's own, so the still and the player occupy the identical box.
 *
 * Hidden below `lg`: the address beside it wraps far wider than its designed 276px on a phone, and the
 * drawing would land on top of the text.
 */
const ILLUSTRATION_BOX =
	'pointer-events-none absolute top-[26px] left-[51%] hidden aspect-[370/358] w-[46.6%] lg:block';

/** "¿Quieres visitarnos?" — where we are and how to phone, on the pale olive card. */
function Whereabouts({ sent }: { sent: boolean }) {
	const { t } = useLingui();
	const reduce = usePrefersReducedMotion();
	return (
		<Expand index={0.5} asChild>
			<div className="bg-sage relative flex flex-col gap-8 overflow-hidden rounded-3xl p-10 lg:h-[308px]">
				{/* One glow, centred at 50.5%/131% of the card: that is where the hand-off's ellipse sits, and
				    the three others it stacks here fall almost entirely outside the card. */}
				<DiamondGrid color="#35433F" opacity={1} {...DIAMOND_TILE} masks={[diamondGlows({ x: '50.5%', y: '131%' })]} />

				{/* Still until the message is away, then it draws itself once — the trim-path animation is
				    built from this very SVG's own paths, so the last frame and the still are the same drawing
				    and the swap cannot jump. `loop={false}`: it is a confirmation, not decoration.

				    Under `prefers-reduced-motion` the still stays. The player renders a paused first frame in
				    that case, and the first frame of a draw-on is an empty box. */}
				{sent && !reduce ? (
					<LottiePlayer
						animationData={mailboxAnimation}
						loop={false}
						ariaLabel={t`An envelope going into a letterbox`}
						className={ILLUSTRATION_BOX}
					/>
				) : (
					<img
						src="/redesign/contact/mailbox.svg"
						alt={t`An envelope going into a letterbox`}
						className={ILLUSTRATION_BOX}
					/>
				)}

				<ContactDetail icon={<LocationIcon className="size-4" />} label={t`Want to visit us?`}>
					<a
						href={MAPS_URL}
						target="_blank"
						rel="noopener noreferrer"
						className="hover:text-brand-strong max-w-[276px] leading-[1.5] transition-colors"
					>
						{ADDRESS_LINES.map((line) => (
							<span key={line} className="block">
								{line}
							</span>
						))}
					</a>
				</ContactDetail>

				<ContactDetail icon={<CallIcon className="size-4" />} label={t`You can also call us`}>
					<a href={PHONE_HREF} className="hover:text-brand-strong leading-[1.5] transition-colors">
						{PHONE_DISPLAY}
					</a>
				</ContactDetail>
			</div>
		</Expand>
	);
}

/**
 * The Contact page's left column: a photograph, the partner-NGO strip, and the address card. 793 wide
 * against the panel's 559, with the hand-off's 16px gutters — not the 24px the rest of the site uses.
 *
 * Every block keeps the height it is drawn at (357 + 104 + 308 + two 16px gutters = the panel's 801),
 * and the column does NOT stretch. The hand-off has both columns ending together, but it only ever
 * draws an 801px panel: letting the photograph absorb the difference instead made it grow past 900px
 * as a visitor ticked their way through the company questionnaire, which reads as a bug.
 */
export function ContactInfo({ audience, sent }: { audience: Audience | null; sent: boolean }) {
	const { t } = useLingui();
	return (
		<div className="flex flex-col gap-4">
			<Expand index={0} asChild>
				<div className="relative aspect-[793/357] overflow-hidden rounded-3xl">
					<Image
						src="/redesign/contact/hero.jpg"
						alt={t`Children smiling outside their school`}
						height={682}
						aspectRatio={1024 / 682}
						objectFit="cover"
						priority
						className="absolute inset-0 size-full object-cover!"
					/>
				</div>
			</Expand>
			<Suspense fallback={<div className="h-[104px]" />}>
				<PartnerLogos audience={audience} />
			</Suspense>
			<Whereabouts sent={sent} />
		</div>
	);
}
